import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take, switchMap, catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { Observable } from 'rxjs';

import { AppState } from '../app.state';
import { selectIsAuthenticated, selectUserUuid } from '../user/user.selectors';
import { OrderService } from '../../services/order.service';
import { ProductV2, ProductV2Variation } from '../../models/product-v2.model';
import { Draft } from '../../models/order.model';
import { AvailableOffersFacade } from '../available-offers/available-offers.facade';
import { isCartHydrationBlocked } from '../../utils/cart-hydration.util';
import { shouldSkipCampaignCartHydration } from '../../campaign/campaign-cart-reset';
import { readStoredExternalChannelCampaign } from '../../utils/external-channel-access.util';

import { selectCartItems } from './cart.selectors';
import { loadCartSuccess, setLoading } from './cart.actions';
import { CartPersistenceService } from './cart-persistence.service';
import { CartFacade } from './cart.facade';
import { CartItem, createCartItem } from './cart.state';

@Injectable({
  providedIn: 'root',
})
export class CartInitService {
  private store = inject(Store<AppState>);
  private cartPersistenceService = inject(CartPersistenceService);
  private orderService = inject(OrderService);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private cartFacade = inject(CartFacade);

  initializeAvailableOffers(): void {
    this.store.select(selectCartItems).pipe(take(1)).subscribe(cartItems => {
      if (cartItems.length > 0) {
        this.mapCartItemsToOffers(cartItems);
      }
    });
  }

  initializeCart(): void {
    if (shouldSkipCampaignCartHydration()) {
      this.cartPersistenceService.clearCartFromLocalStorage();
      return;
    }
    this.store
      .select(selectIsAuthenticated)
      .pipe(take(1))
      .subscribe(isAuthenticated => {
        if (!isAuthenticated) {
          const storedItems = this.cartPersistenceService.loadCartFromLocalStorage();
          if (storedItems.length > 0) {
            this.mapCartItemsToOffers(storedItems);
            this.store.dispatch(loadCartSuccess({ items: storedItems, source: 'localStorage' }));
          }
        }
      });
  }

  clearStoredCartOnLogin(): void {
    this.store
      .select(selectIsAuthenticated)
      .pipe(
        filter(isAuthenticated => isAuthenticated),
        take(1)
      )
      .subscribe(() => {
        this.syncLocalStorageIfNeeded();
      });
  }

  private syncLocalStorageIfNeeded(): void {
    if (isCartHydrationBlocked() || shouldSkipCampaignCartHydration()) {
      return;
    }

    const storedItems = this.cartPersistenceService.loadCartFromLocalStorage();
    if (readStoredExternalChannelCampaign() && storedItems.length === 0) {
      this.cartFacade.clearCart();
      return;
    }
    if (storedItems.length > 0) {
      this.sendDraftToBackend(storedItems);
    } else {
      // Se não há itens no localStorage, carrega do backend
      this.loadCartFromBackend().pipe(take(1)).subscribe({
        next: (draft) => {
          this.store.select(selectCartItems).pipe(take(1)).subscribe(currentItems => {
            
            if (currentItems.length === 0 && draft && draft.orderItems && draft.orderItems.length > 0) {
              const cartItems = this.convertBackendToCartItems(draft);
              if (cartItems.length > 0) {
                this.store.dispatch(loadCartSuccess({ items: cartItems, source: 'backend' }));
              }
            }
          });
        },
        error: (_error) => {
          console.error('Erro ao carregar carrinho do backend em syncLocalStorageIfNeeded:', _error);
        }
      });
    }
  }

  private sendDraftToBackend(items: CartItem[]): void {
    this.store
      .select(selectUserUuid)
      .pipe(
        take(1),
        switchMap(userUuid => {
          if (!userUuid) {
            throw new Error('UUID do usuário não encontrado');
          }

          const draftRequest: Draft = {
            userUuid: userUuid,
            draftOrderUuid: null,
            orderItems: items.map(item => ({
              product: item.product,
              quantity: item.quantity,
              selectedVariation:
                item.selectedVariation || item.product.variations?.[0],
            })),
          };
          return this.orderService.createOrderDraft(draftRequest);
        }),
        switchMap(_response => {
          this.cartPersistenceService.clearCartFromLocalStorage();
          return of(null);
        }),
        catchError(_error => {
          this.cartPersistenceService.clearCartFromLocalStorage();
          return of(null);
        })
      )
      .subscribe({
        error: (_error) => {
          console.error('Erro ao carregar carrinho do backend:', _error);
        }
      });
  }

  private loadCartFromBackend(): Observable<Draft | null> {
    if (isCartHydrationBlocked() || shouldSkipCampaignCartHydration()) {
      return of(null);
    }

    return this.store.select(selectUserUuid).pipe(
      take(1),
      switchMap(userUuid => {
        if (!userUuid) return of(null);

        this.store.dispatch(setLoading({ loading: true }));

        return this.orderService.getOrderDraftByUuid(userUuid).pipe(
          take(1),
          switchMap((orderDraft: Draft) => {
            if (
              orderDraft?.orderItems &&
              Array.isArray(orderDraft.orderItems) &&
              orderDraft.orderItems.length > 0
            ) {
              return this.loadProductsDetails(orderDraft);
            }

            return of(orderDraft);
          }),
          finalize(() => this.store.dispatch(setLoading({ loading: false })))
        );
      })
    );
  }

  private loadProductsDetails(orderDraft: Draft): Observable<Draft> {
    const cartItems = this.convertBackendToCartItems(orderDraft);

    if (cartItems.length > 0) {
      this.mapCartItemsToOffers(cartItems);
    }

    // Só carrega do backend se não há itens no estado atual
    this.store.select(selectCartItems).pipe(take(1)).subscribe(currentItems => {if (currentItems.length === 0 && cartItems.length > 0) {
        this.store.dispatch(loadCartSuccess({ items: cartItems, source: 'backend' }));
      }
    });

    return of(orderDraft);
  }

private convertBackendToCartItems(orderDraft: Draft): CartItem[] {
  try {
    if (!Array.isArray(orderDraft?.orderItems)) return [];

    return orderDraft.orderItems
      .filter((op) => Boolean(op?.product?.uuid))
      .map((op) => {
        const product   = op.product as ProductV2;
        const variation = op.selectedVariation as ProductV2Variation;
          
        if (!product?.uuid) {
            console.warn('Produto inválido no draft:', op);
            return null;
        }

        const cartItem = createCartItem(
          {
            ...product,
            id: product.id || 0,
            uuid: product.uuid,
            name: product.name ?? `Produto ${product.uuid}`,
            salesPrice: product.salesPrice ?? 0,
            price: product.price ?? 0,
            variations: product.variations ?? [],
            description: product.description ?? '',
            image: product.image ?? '',
            sku: product.sku ?? '',
          },
          op.quantity || 1,
          variation
        );

        return cartItem;
      })
      .filter((item): item is CartItem => item !== null);
    } catch (error) {
      console.error('Erro ao converter backend para CartItems:', error);
      return [];
    }
  }

  private mapCartItemsToOffers(cartItems: CartItem[]): void {
    const productsMap = new Map<string, ProductV2>();
    
    cartItems.forEach(item => {
      if (item.product && !productsMap.has(item.product.uuid)) {
        productsMap.set(item.product.uuid, item.product);
      }
    });

    productsMap.forEach(product => {
      this.availableOffersFacade.mapProductVariationsToOffers(product);
    });
  }
}
